import { useCallback, useEffect, useMemo } from "react";

import { useDispatch, useSelector } from "react-redux";

import type { SelectionDropdownItem } from "app/pages/sales-intelligence/common-components/table-actions-panel";
import { ManualSelectionItem } from "app/pages/sales-intelligence/common-components/table-actions-panel";
import {
    selectActiveLeadsType,
    selectSelectedRowDomains,
    setIsRowSelectionEnabled,
    useClearAndCloseRowsSelection,
} from "app/pages/sales-intelligence/modules/leads-search";
import { useExportQuota } from "app/pages/sales-intelligence/modules/quota/hooks/useExportQuota";
import type { QuotaOperationState } from "app/pages/sales-intelligence/modules/quota/types";
import type { LeadsType } from "app/pages/sales-intelligence/sub-modules/company-search/types/common";
import { hideExportSuccessToasts } from "app/pages/sales-intelligence/sub-modules/contacts-search/helpers/exportFlow";
import createCRMLeadsSelectionDropdown from "app/pages/sales-intelligence/sub-modules/management/models/websites-crm-export/factory";

type HookValue = {
    excelQuota: QuotaOperationState;
    selectedRowDomains: readonly string[];
    leadsSelectionDropdown: ReturnType<typeof createCRMLeadsSelectionDropdown>;
    onSelectionChange: (item: SelectionDropdownItem) => void;
    onClearAndDeactivate: VoidFunction;
};

export const useCrmLeadsExportPanel = (onDeactivate: VoidFunction): HookValue => {
    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(hideExportSuccessToasts());
    }, []);

    const activeLeadsType = useSelector(selectActiveLeadsType);
    const selectedRowDomains = useSelector(selectSelectedRowDomains);

    const { data: excelQuota } = useExportQuota();

    const clearAndCloseRowsSelection = useClearAndCloseRowsSelection();

    const onClearAndDeactivate = useCallback((): void => {
        clearAndCloseRowsSelection();
        onDeactivate();
    }, [clearAndCloseRowsSelection, onDeactivate]);

    const leadsSelectionDropdown = useMemo(
        () => createCRMLeadsSelectionDropdown(activeLeadsType as LeadsType),
        [activeLeadsType],
    );

    const onSelectionChange = useCallback(
        (item: SelectionDropdownItem): void => {
            if (item.id === ManualSelectionItem.ID) {
                dispatch(setIsRowSelectionEnabled(true));

                return;
            }

            clearAndCloseRowsSelection();
        },
        [clearAndCloseRowsSelection],
    );

    return {
        excelQuota,
        selectedRowDomains,
        leadsSelectionDropdown,

        onSelectionChange,
        onClearAndDeactivate,
    };
};
